import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/brand";
import BackToAdminLink from "@/components/BackToAdminLink";
import {
  Tag,
  Plus,
  PencilSimple,
  Trash,
  FloppyDisk,
  X,
  ArrowClockwise,
  Copy,
  CheckCircle,
} from "@phosphor-icons/react";

// Iter37 — URL-based campaigns admin. Custom-effect promotional offers (free
// months on annual, extended trials) live here. Native money-off codes stay in
// the Stripe Dashboard.

// Iter38.2 — Campaign share URLs point at the *marketing site* now (not the
// app root). Reason: hitting the app root sends the visitor straight to Stripe
// checkout and skips the reassurance layer (banner + pricing chips). Sending
// them via forecastly.me/pricing lets them see the offer, choose their plan,
// and *then* land at Stripe with the campaign already applied.
const MARKETING_URL = process.env.REACT_APP_MARKETING_URL || "https://forecastly.me";
const EMPTY_FORM = {
  slug: "",
  internal_name: "",
  display_name: "",
  anchor_shift_days: 90,
  trial_days_bonus: 0,
  forced_plan: "annual",
  max_redemptions: 100,
  expires_at_local: "",
  active: true,
  notes: "",
};

// Convert an ISO string → the local datetime-input value the browser expects.
function toLocalDatetimeInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalDatetimeInput(local) {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

// Auto-generate a URL slug from an internal name.
function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-")
    .slice(0, 64);
}

export default function AdminCampaignsPage() {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // form state + id (or isNew: true)
  const [busy, setBusy] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/campaigns`);
      setCampaigns(res.data.campaigns || []);
    } catch (e) {
      toast.error("Could not load campaigns");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_admin]);

  const startNew = () => setEditing({ isNew: true, ...EMPTY_FORM });
  const startEdit = (c) =>
    setEditing({
      id: c.id,
      slug: c.slug,
      internal_name: c.internal_name,
      display_name: c.display_name === c.internal_name ? "" : (c.display_name || ""),
      anchor_shift_days: c.anchor_shift_days || 0,
      trial_days_bonus: c.trial_days_bonus || 0,
      forced_plan: c.forced_plan || "",
      max_redemptions: c.max_redemptions ?? 0,
      expires_at_local: toLocalDatetimeInput(c.expires_at),
      active: !!c.active,
      notes: c.notes || "",
    });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    if ((editing.internal_name || "").trim().length < 2) {
      toast.error("Give the campaign a memorable internal name");
      return;
    }
    const anchor = Number(editing.anchor_shift_days) || 0;
    const trial = Number(editing.trial_days_bonus) || 0;
    if (!anchor && !trial) {
      toast.error("Campaign needs at least one benefit (free months or trial days)");
      return;
    }
    const forced = editing.forced_plan || null;
    if (anchor > 0 && forced === "monthly") {
      toast.error("Anchor-shift is annual-only — set forced plan to Annual or clear the free-months field.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        internal_name: editing.internal_name.trim(),
        display_name: (editing.display_name || "").trim(),
        anchor_shift_days: anchor,
        trial_days_bonus: trial,
        forced_plan: forced || null,
        max_redemptions: Number(editing.max_redemptions) || 0,
        expires_at: fromLocalDatetimeInput(editing.expires_at_local),
        active: !!editing.active,
        notes: (editing.notes || "").trim(),
      };
      if (editing.isNew) {
        const slug = slugify(editing.slug || editing.internal_name);
        if (slug.length < 3) {
          toast.error("Slug is too short");
          setBusy(false);
          return;
        }
        await axios.post(`${API}/admin/campaigns`, { slug, ...payload });
        toast.success("Campaign created");
      } else {
        await axios.patch(`${API}/admin/campaigns/${editing.id}`, payload);
        toast.success("Campaign updated");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save campaign");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.internal_name}"? Redemption history is lost too.`)) return;
    try {
      await axios.delete(`${API}/admin/campaigns/${c.id}`);
      toast.success("Campaign deleted");
      load();
    } catch (e) {
      toast.error("Could not delete campaign");
    }
  };

  const toggleActive = async (c) => {
    try {
      await axios.patch(`${API}/admin/campaigns/${c.id}`, { active: !c.active });
      toast.success(c.active ? "Campaign paused" : "Campaign live");
      load();
    } catch (e) {
      toast.error("Could not update campaign");
    }
  };

  const copyLink = (slug) => {
    const url = `${MARKETING_URL}/pricing?offer=${slug}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedSlug(slug);
      toast.success("Share link copied");
      setTimeout(() => setCopiedSlug(""), 2500);
    });
  };

  const shareLink = (c) => `${MARKETING_URL}/pricing?offer=${c.slug}`;

  if (user && !user.is_admin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-6" data-testid="admin-campaigns-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <BackToAdminLink />
          <div className="section-eyebrow flex items-center gap-2">
            <Tag size={16} weight="bold" /> Admin · Campaigns
          </div>
          <h1 className="font-heading uppercase text-4xl mt-2">URL campaigns</h1>
          <p className="text-black/75 mt-3 leading-relaxed max-w-2xl">
            Special-offer URLs for webinars, partners, launches. Each campaign generates a
            shareable link that lands visitors on the marketing site&apos;s pricing page with
            the offer banner and pricing chips already visible — no code entry required.
            The offer is then applied invisibly at Stripe checkout, on the welcome email,
            and on the Account page. Native money-off codes (like <em>LAUNCH50</em>) stay
            in the Stripe Dashboard.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="btn-primary"
            onClick={startNew}
            data-testid="admin-campaign-add-btn"
          >
            <Plus size={14} weight="bold" /> New campaign
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={load}
            disabled={loading}
            data-testid="admin-campaign-reload"
          >
            <ArrowClockwise size={14} weight="bold" /> {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {editing && (
        <div className="card border-2 border-black" data-testid="admin-campaign-editor">
          <div className="flex items-center justify-between">
            <h2 className="font-heading uppercase text-xl">
              {editing.isNew ? "New campaign" : `Edit ${editing.internal_name || "campaign"}`}
            </h2>
            <button
              type="button"
              onClick={cancel}
              className="text-black/60 hover:text-black"
              data-testid="admin-campaign-close"
            >
              <X size={20} weight="bold" />
            </button>
          </div>

          <div className="mt-4 grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold mb-1">Internal name</label>
              <input
                type="text"
                className="input"
                value={editing.internal_name}
                onChange={(e) => {
                  const v = e.target.value;
                  setEditing((s) => ({
                    ...s,
                    internal_name: v,
                    // Only auto-generate slug on NEW campaigns and while slug hasn't been touched
                    slug: s.isNew && !s._slugTouched ? slugify(v) : s.slug,
                  }));
                }}
                placeholder="e.g. Webinar Feb 2026"
                maxLength={200}
                autoFocus
                data-testid="admin-campaign-name"
              />
              <p className="text-[11px] text-black/50 mt-1">Only shown to you.</p>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">
                Public display name <span className="text-black/50">(shown on the banner)</span>
              </label>
              <input
                type="text"
                className="input"
                value={editing.display_name}
                onChange={(e) =>
                  setEditing((s) => ({ ...s, display_name: e.target.value }))
                }
                placeholder={editing.internal_name ? `Defaults to "${editing.internal_name}"` : "e.g. Amplify Impact webinar"}
                maxLength={200}
                data-testid="admin-campaign-display-name"
              />
              <p className="text-[11px] text-black/50 mt-1">
                Shown on the marketing site's sticky offer banner. Leave blank to fall
                back to the internal name.
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">
                URL slug {editing.isNew ? "" : <span className="text-black/50">(cannot change)</span>}
              </label>
              <input
                type="text"
                className="input"
                value={editing.slug}
                disabled={!editing.isNew}
                onChange={(e) =>
                  setEditing((s) => ({
                    ...s,
                    slug: slugify(e.target.value),
                    _slugTouched: true,
                  }))
                }
                placeholder="webinar-feb-2026"
                maxLength={64}
                data-testid="admin-campaign-slug"
              />
              <p className="text-[11px] text-black/50 mt-1">
                Lowercase letters, digits, hyphens. Appears in the share URL.
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">
                Free months (annual only)
              </label>
              <div className="flex gap-2 items-center">
                <input
                  type="number"
                  min={0}
                  max={24}
                  className="input"
                  value={
                    Math.round((Number(editing.anchor_shift_days) || 0) / 30)
                  }
                  onChange={(e) =>
                    setEditing((s) => ({
                      ...s,
                      anchor_shift_days: (Number(e.target.value) || 0) * 30,
                    }))
                  }
                  data-testid="admin-campaign-anchor-months"
                />
                <span className="text-sm text-black/60">
                  = {Number(editing.anchor_shift_days) || 0} days
                </span>
              </div>
              <p className="text-[11px] text-black/50 mt-1">
                First billing cycle is extended by this many months.
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">Extra trial days</label>
              <input
                type="number"
                min={0}
                max={365}
                className="input"
                value={editing.trial_days_bonus}
                onChange={(e) =>
                  setEditing((s) => ({
                    ...s,
                    trial_days_bonus: Number(e.target.value) || 0,
                  }))
                }
                data-testid="admin-campaign-trial-bonus"
              />
              <p className="text-[11px] text-black/50 mt-1">
                Added on top of the standard 10-day trial.
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">Force plan</label>
              <select
                className="input"
                value={editing.forced_plan || ""}
                onChange={(e) =>
                  setEditing((s) => ({ ...s, forced_plan: e.target.value || "" }))
                }
                data-testid="admin-campaign-forced-plan"
              >
                <option value="">Let user choose</option>
                <option value="annual">Annual only</option>
                <option value="monthly">Monthly only</option>
              </select>
              <p className="text-[11px] text-black/50 mt-1">
                Free-months offers must be annual.
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">
                Max redemptions (0 = unlimited)
              </label>
              <input
                type="number"
                min={0}
                max={100000}
                className="input"
                value={editing.max_redemptions ?? 0}
                onChange={(e) =>
                  setEditing((s) => ({
                    ...s,
                    max_redemptions: Number(e.target.value) || 0,
                  }))
                }
                data-testid="admin-campaign-max"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">Expires at (local time)</label>
              <input
                type="datetime-local"
                className="input"
                value={editing.expires_at_local}
                onChange={(e) =>
                  setEditing((s) => ({ ...s, expires_at_local: e.target.value }))
                }
                data-testid="admin-campaign-expires"
              />
              <p className="text-[11px] text-black/50 mt-1">
                Leave blank to run indefinitely.
              </p>
            </div>

            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!editing.active}
                  onChange={(e) =>
                    setEditing((s) => ({ ...s, active: e.target.checked }))
                  }
                  className="w-4 h-4"
                  data-testid="admin-campaign-active"
                />
                <span>
                  <strong>Active</strong> — new signups can redeem. Uncheck to pause.
                </span>
              </label>
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-semibold mb-1">Internal notes</label>
              <textarea
                className="input min-h-[60px]"
                value={editing.notes}
                onChange={(e) =>
                  setEditing((s) => ({ ...s, notes: e.target.value }))
                }
                placeholder="How was this shared? Which audience?"
                maxLength={2000}
                data-testid="admin-campaign-notes"
              />
            </div>
          </div>

          <div className="mt-5 flex gap-3">
            <button
              type="button"
              className="btn-primary"
              onClick={save}
              disabled={busy}
              data-testid="admin-campaign-save"
            >
              <FloppyDisk size={14} weight="bold" /> {busy ? "Saving…" : "Save campaign"}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={cancel}
              data-testid="admin-campaign-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3" data-testid="admin-campaign-list">
        {loading && campaigns.length === 0 ? (
          <p className="text-center text-black/55 py-8">Loading…</p>
        ) : campaigns.length === 0 ? (
          <div className="card text-center">
            <p className="text-black/65">
              No campaigns yet. Click "New campaign" to create your first offer link.
            </p>
          </div>
        ) : (
          campaigns.map((c) => {
            const expired = c.expires_at && new Date(c.expires_at) <= new Date();
            const maxed = c.max_redemptions && c.redemptions_count >= c.max_redemptions;
            const live = c.active && !expired && !maxed;
            const link = shareLink(c);
            const copied = copiedSlug === c.slug;
            return (
              <div
                key={c.id}
                className="card !p-4"
                data-testid={`admin-campaign-row-${c.slug}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading uppercase text-lg">{c.internal_name}</h3>
                      <span
                        className={`text-[9px] font-heading uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                          live
                            ? "bg-emerald-100 text-emerald-800 border-emerald-700"
                            : c.active
                            ? "bg-amber-100 text-amber-800 border-amber-700"
                            : "bg-black/10 text-black/60 border-black/30"
                        }`}
                        data-testid={`admin-campaign-status-${c.slug}`}
                      >
                        {live ? "LIVE" : expired ? "EXPIRED" : maxed ? "SOLD OUT" : "PAUSED"}
                      </span>
                    </div>
                    <p className="text-sm text-black/70 mt-1">
                      <span className="font-semibold">{c.human_summary}</span>
                      {c.forced_plan ? ` · ${c.forced_plan} only` : ""}
                    </p>
                    {c.display_name && c.display_name !== c.internal_name && (
                      <p className="text-xs text-black/55 mt-1">
                        Banner: <span className="font-semibold">{c.display_name}</span>
                      </p>
                    )}
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-black/60">
                      <span>
                        Redeemed:{" "}
                        <strong>
                          {c.redemptions_count}
                          {c.max_redemptions ? ` / ${c.max_redemptions}` : ""}
                        </strong>
                      </span>
                      {c.expires_at && (
                        <span>
                          Expires:{" "}
                          <strong>
                            {new Date(c.expires_at).toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </strong>
                        </span>
                      )}
                    </div>
                    {c.notes && (
                      <p className="text-xs text-black/55 mt-2 italic whitespace-pre-wrap">
                        {c.notes}
                      </p>
                    )}
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <code className="text-xs bg-[#FFF7E5] border border-black/20 rounded px-2 py-1 font-mono select-all break-all">
                        {link}
                      </code>
                      <button
                        type="button"
                        onClick={() => copyLink(c.slug)}
                        className="!px-2 !py-1 !text-xs rounded-lg border-[1.5px] border-black/40 hover:bg-[#FFBD4A] font-semibold inline-flex items-center gap-1"
                        data-testid={`admin-campaign-copy-${c.slug}`}
                      >
                        {copied ? (
                          <>
                            <CheckCircle size={12} weight="fill" /> Copied
                          </>
                        ) : (
                          <>
                            <Copy size={12} weight="bold" /> Copy link
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => startEdit(c)}
                      className="btn-secondary !px-2 !py-1 !text-xs"
                      data-testid={`admin-campaign-edit-${c.slug}`}
                    >
                      <PencilSimple size={12} weight="bold" /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleActive(c)}
                      className="btn-secondary !px-2 !py-1 !text-xs"
                      data-testid={`admin-campaign-toggle-${c.slug}`}
                    >
                      {c.active ? "Pause" : "Activate"}
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(c)}
                      className="!px-2 !py-1 !text-xs rounded-lg border-[1.5px] border-red-600 text-red-700 hover:bg-red-50 font-semibold inline-flex items-center gap-1"
                      data-testid={`admin-campaign-delete-${c.slug}`}
                    >
                      <Trash size={12} weight="bold" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
